import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Download, FileText, Loader2, Sparkles, Trash2, Upload } from 'lucide-react';
import { SidePanel } from '../components/SidePanel';
import { useAlert } from '../contexts/AlertContext';
import { http } from '../services/http';
import { Company } from '../types/company';
import { Vehicle } from '../types/vehicle';
import { companyApi } from '../services/companyApi';
import { vehicleApi } from '../services/vehicleApi';
import { ACCEPT, DOC_KIND, DOC_STATUS, Doc, DocField, documentApi, guessKind } from '../services/documentApi';
import { Tour, TourButton, TourStep } from '../components/Tour';

/**
 * 문서함 — 투자처가 보낸 자료를 올리면 AI 가 읽어 칸을 채운다 (2026-10-03: 투자처 계정 대신).
 *
 * ⚠️ 추출값은 **검수해야 반영된다.** 확신도가 낮은 칸(0.85 미만)은 노랗게 띄운다.
 * 지금 AI 가 읽는 것은 재무제표(PDF · 이미지 · CSV)뿐이다 — 재무 표의 칸으로 들어간다.
 * 같은 투자처에 같은 파일(내용 기준)은 두 번 올라가지 않는다.
 *
 * 2026-11-02 점검: 목록은 넓게 · 상세는 오른쪽 패널(UI 규칙) · 지우기는 공통 확인창 · 투자처 · 자금원 · 종류로 거르기.
 * 「공유 범위」는 화면에서 뺐다 — 투자처 · LP 에 자료를 보내는 기능이 없어 쓰는 곳이 없다(열은 DB 에 남는다).
 * 서버가 AI 로 문서를 보내지 않게 설정돼 있으면(AI_SEND_SENSITIVE) 「AI 로 읽기」 대신 안내를 띄운다.
 */
const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-600', extracted: 'bg-amber-50 text-amber-700', checked: 'bg-emerald-50 text-emerald-700', skipped: 'bg-slate-100 text-slate-400',
};
const LOW = 0.85;

/**
 * 회계기간 → 어느 재무 줄에 들어가나 (#77) — 연간(FY) · 상반기(H1)만 자동, 나머지는 null(반영 안 함).
 * 서버 documents.service `periodKind` 와 같은 규칙 — 고칠 때 같이 본다.
 */
function periodKind(v: string | undefined, fiscalMonth: number | null | undefined): 'FY' | 'H1' | null {
  if (!v) return 'FY';
  const m = /^(?:(\d{4}-\d{2}-\d{2}))?\s*~\s*(\d{4}-\d{2}-\d{2})$/.exec(v.trim());
  if (!m) return null;
  const fm = fiscalMonth && fiscalMonth >= 1 && fiscalMonth <= 12 ? fiscalMonth : 12;
  const half = ((fm + 5) % 12) + 1;
  const endMonth = Number(m[2].slice(5, 7));
  const raw = m[1] ? Math.round((Date.parse(`${m[2]}T00:00:00Z`) - Date.parse(`${m[1]}T00:00:00Z`)) / 86_400_000) + 1 : null;
  if (raw !== null && !(Number.isFinite(raw) && raw > 0)) return null;
  if (raw !== null && raw >= 360) return 'FY';
  if (endMonth === fm) return 'FY';
  if (endMonth === half && (raw === null || (raw >= 150 && raw <= 210))) return 'H1';
  return null;
}
const dot = (s: string) => s.slice(0, 10).replace(/-/g, '.');
const KIND_LABEL: Record<string, string> = { financials: '재무제표', biz_reg: '사업자등록증', corp_reg: '법인등기부등본', investment_contract: '투자계약서', fund_agreement: '조합규약', ir: 'IR', shareholders: '주주명부', other: '기타' };
const sel = 'rounded border border-slate-200 px-1.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200';

export const DocumentsPage: React.FC = () => {
  const { showConfirm, showToast } = useAlert();
  const [params, setParams] = useSearchParams();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [fields, setFields] = useState<DocField[]>([]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [values, setValues] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'' | 'upload' | 'extract' | 'apply'>('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [panelMsg, setPanelMsg] = useState<{ ok: boolean; text: string } | null>(null);
  /** 서버가 문서를 AI 로 보내나 — 모르면(상태 조회 실패) 버튼을 그대로 둔다. 서버도 막는다 */
  const [aiSend, setAiSend] = useState<boolean | null>(null);
  const [fCompany, setFCompany] = useState('');
  const [fVehicle, setFVehicle] = useState('');
  const [fKind, setFKind] = useState('');
  const [tourRun, setTourRun] = useState(false);

  const pick = Number(params.get('id')) || null;
  const doc = docs.find((d) => d.id === pick);
  const setPick = (id: number | null) => { setPanelMsg(null); setParams(id ? { id: String(id) } : {}, { replace: true }); };

  const load = useCallback(async () => {
    const [ds, cs, vs] = await Promise.all([documentApi.list(), companyApi.list(), vehicleApi.list()]);
    setDocs(ds); setCompanies(cs); setVehicles(vs); setLoading(false);
    return ds;
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    http.get<{ sendSensitive?: boolean }>('/ai/status').then((s) => setAiSend(s.sendSensitive ?? null)).catch(() => setAiSend(null));
  }, []);

  const loadFields = useCallback(async (id: number) => {
    const fs = await documentApi.fields(id);
    setFields(fs);
    // 반영할 수 있는 칸(재무)은 처음부터 골라 둔다 — 빼고 싶은 것만 끈다
    setChecked(new Set(fs.filter((f) => f.target?.startsWith('financials.')).map((f) => f.id)));
    setValues({});
  }, []);
  useEffect(() => { if (pick) loadFields(pick).catch(() => setFields([])); else setFields([]); }, [pick, loadFields]);

  const cName = (id: number | null) => companies.find((c) => c.id === id)?.name;
  const vName = (id: number | null) => vehicles.find((v) => v.id === id)?.name;

  const shown = docs.filter((d) =>
    (!fCompany || (fCompany === 'none' ? d.companyId === null && d.vehicleId === null : d.companyId === Number(fCompany)))
    && (!fVehicle || d.vehicleId === Number(fVehicle))
    && (!fKind || d.kind === fKind));
  const filtered = !!(fCompany || fVehicle || fKind);

  /** 파일명으로 투자처를 짐작한다 — 틀리면 상세에서 고친다 */
  const guessCompany = (name: string) => {
    const n = name.replace(/\s/g, '');
    return companies.find((c) => n.includes(c.name.replace(/\s/g, '')))?.id ?? null;
  };
  const addFiles = async (files: FileList | null) => {
    const list = Array.from(files ?? []).filter((f) => !f.name.startsWith('.'));
    if (list.length === 0) return;
    setBusy('upload'); setMsg(null);
    const errs: string[] = [];
    let last: number | null = null;
    for (const f of list) {
      try {
        const kind = guessKind(f.name);
        const companyId = guessCompany(f.name);
        const d = await documentApi.upload(f, { companyId, kind, vehicleId: kind === 'fund_agreement' && !companyId ? vehicles.find((v) => f.name.includes(v.name.replace(/ 위탁분$/, '')))?.id : null });
        last = d.id;
      } catch (e) { errs.push(`${f.name} — ${(e as Error).message}`); }
    }
    setBusy('');
    await load();
    // 한 개만 올렸으면 바로 열어 종류 · 투자처를 확인하게 한다
    if (last && list.length === 1) setPick(last);
    setMsg(errs.length ? { ok: false, text: errs.join(' · ') } : { ok: true, text: `${list.length}개 올림` });
  };

  const patch = async (p: Partial<Doc>) => {
    if (!doc) return;
    try { await documentApi.update(doc.id, p); await load(); } catch (e) { setPanelMsg({ ok: false, text: (e as Error).message }); }
  };
  const extract = async () => {
    if (!doc) return;
    setBusy('extract'); setPanelMsg(null);
    try { await documentApi.extract(doc.id); await load(); await loadFields(doc.id); } catch (e) { setPanelMsg({ ok: false, text: (e as Error).message }); } finally { setBusy(''); }
  };
  const apply = async () => {
    if (!doc) return;
    setBusy('apply'); setPanelMsg(null);
    try {
      await documentApi.apply(doc.id, Array.from(checked), values);
      await load(); await loadFields(doc.id);
      setPanelMsg({ ok: true, text: '재무에 반영' });
    } catch (e) { setPanelMsg({ ok: false, text: (e as Error).message }); } finally { setBusy(''); }
  };
  const remove = async () => {
    if (!doc) return;
    const ok = await showConfirm(`「${doc.fileName}」 을(를) 지웁니다. 뽑아 둔 추출값도 함께 사라집니다.`, { title: '문서 지우기', confirmText: '지우기', danger: true });
    if (!ok) return;
    try {
      await documentApi.remove(doc.id);
      setPick(null); await load();
      showToast('문서를 지웠습니다', 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  };

  // 회계기간 — 연간 · 상반기 줄을 가른다. 정할 수 없으면 반영하지 않는다(서버도 막는다, #64 · #77)
  const period = fields.find((f) => f.target === 'period');
  const kind = doc?.kind === 'financials' ? periodKind(period?.value, companies.find((c) => c.id === doc?.companyId)?.fiscalMonth) : 'FY';
  const canApply = doc?.extractStatus === 'extracted' && kind !== null && fields.some((f) => checked.has(f.id));
  const canExtract = doc?.kind === 'financials' && doc.extractStatus !== 'checked';

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'drop', target: '[data-tour="drop"]', title: '올리기', content: '재무제표 · 사업자등록증 · 등기부등본 · 투자계약서 · 조합 규약 · 주주명부. PDF · 엑셀 · 한글 · 사진, 30MB 까지. 파일명으로 종류와 투자처를 짐작합니다. 같은 투자처에 같은 파일은 두 번 올라가지 않습니다.' },
    { name: 'filter', target: '[data-tour="filter"]', title: '거르기', content: '투자처 · 자금원 · 종류로 거릅니다.' },
    { name: 'list', target: '[data-tour="list"]', title: '문서', content: '누르면 오른쪽에 상세가 열립니다. 종류 · 투자처를 고치고, 재무제표는 AI 로 읽어 검수한 뒤 투자처 재무에 넣습니다. 노란 칸은 확신도가 낮으니 원문과 대조합니다.' },
  ], []);

  if (loading) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">문서함 <TourButton onClick={() => setTourRun(true)} /></h1>
        <span className="rounded-full px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-600">문서 {docs.length}</span>
      </div>

      <div data-tour="drop" className="flex flex-col items-center justify-center gap-2 mb-5 border-2 border-dashed border-slate-300 rounded-lg bg-white py-6"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
        {busy === 'upload' ? <Loader2 size={18} className="text-indigo-500 animate-spin" /> : <Upload size={18} className="text-slate-400" />}
        <span className="text-sm font-medium text-slate-700">파일이나 폴더를 끌어다 놓기</span>
        <span className="text-xs text-slate-400">PDF · XLSX · CSV · DOCX · HWP · JPG · PNG · 30MB</span>
        <div className="flex gap-2">
          <label className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer">파일 고르기
            <input id="doc-files" type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} /></label>
          <label className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 cursor-pointer">폴더 고르기
            <input id="doc-folder" type="file" multiple className="hidden" {...({ webkitdirectory: '' } as Record<string, string>)} onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} /></label>
        </div>
      </div>

      {msg && <p className={`mb-3 text-xs ${msg.ok ? 'text-emerald-700' : 'text-red-600'}`}>{msg.text}</p>}

      <div data-tour="filter" className="flex flex-wrap items-center gap-2 mb-3">
        <select aria-label="투자처로 거르기" value={fCompany} onChange={(e) => setFCompany(e.target.value)} className={`${sel} bg-white`}>
          <option value="">투자처 전체</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          <option value="none">투자처 · 자금원 미지정</option>
        </select>
        <select aria-label="자금원으로 거르기" value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} className={`${sel} bg-white`}>
          <option value="">자금원 전체</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select aria-label="종류로 거르기" value={fKind} onChange={(e) => setFKind(e.target.value)} className={`${sel} bg-white`}>
          <option value="">종류 전체</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {filtered && (
          <>
            <span className="text-xs text-slate-500">{shown.length}건</span>
            <button onClick={() => { setFCompany(''); setFVehicle(''); setFKind(''); }} className="text-xs text-indigo-600 hover:underline">거르기 풀기</button>
          </>
        )}
      </div>

      <div data-tour="list" className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs text-slate-400">
              <th className="text-left font-medium px-4 py-2.5">파일</th>
              <th className="text-left font-medium px-2 py-2.5">종류</th>
              <th className="text-left font-medium px-2 py-2.5">투자처 · 자금원</th>
              <th className="text-left font-medium px-2 py-2.5">올린 날</th>
              <th className="text-left font-medium px-4 py-2.5">상태</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-sm text-slate-500">{filtered ? '조건에 맞는 문서 없음' : '문서 없음 — 위에 파일을 끌어다 놓으세요'}</td></tr>}
            {shown.map((d) => (
              <tr key={d.id} onClick={() => setPick(d.id)} className={`border-b border-slate-100 last:border-b-0 cursor-pointer ${pick === d.id ? 'bg-indigo-50/60' : 'hover:bg-slate-50'}`}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={15} className="text-slate-400 flex-shrink-0" />
                    <span className="font-medium text-slate-900 truncate max-w-[28rem]">{d.fileName}</span>
                  </div>
                </td>
                <td className="px-2 py-2.5 text-xs text-slate-600 whitespace-nowrap">{DOC_KIND.toUi(d.kind)}</td>
                <td className="px-2 py-2.5 text-xs text-slate-600">{cName(d.companyId) ?? vName(d.vehicleId) ?? <span className="text-amber-700">미지정</span>}</td>
                <td className="px-2 py-2.5 text-xs text-slate-500 tabular-nums whitespace-nowrap">{dot(d.createdAt)}</td>
                <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_STYLE[d.extractStatus]}`}>{DOC_STATUS.toUi(d.extractStatus)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {doc && (
        <SidePanel
          onClose={() => setPick(null)}
          header={
            <div className="min-w-0">
              <div className="text-xs text-slate-500">문서</div>
              <h2 className="mt-0.5 text-base font-semibold text-slate-900 break-all">{doc.fileName}</h2>
              <div className="mt-0.5 text-xs text-slate-400">
                {doc.sizeBytes ? `${Math.max(1, Math.round(doc.sizeBytes / 1024)).toLocaleString()}KB · ` : ''}{dot(doc.createdAt)} 올림
                {doc.companyId && <> · <Link to={`/companies/${doc.companyId}`} className="text-indigo-600 hover:underline">{cName(doc.companyId)} 상세</Link></>}
              </div>
            </div>
          }
          footer={
            <>
              <button onClick={remove} className="mr-auto flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50"><Trash2 size={13} /> 지우기</button>
              <button onClick={() => documentApi.download(doc.id, doc.fileName).catch((e) => showToast((e as Error).message, 'error'))}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"><Download size={13} /> 내려받기</button>
            </>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <label className="text-xs text-slate-500">종류
              <select aria-label="종류" value={doc.kind} onChange={(e) => patch({ kind: e.target.value })} className={`${sel} block w-full mt-1 bg-white`}>
                {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">투자처
              <select aria-label="투자처" value={doc.companyId ?? ''} onChange={(e) => patch({ companyId: e.target.value ? Number(e.target.value) : null })} className={`${sel} block w-full mt-1 bg-white`}>
                <option value="">—</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">자금원
              <select aria-label="자금원" value={doc.vehicleId ?? ''} onChange={(e) => patch({ vehicleId: e.target.value ? Number(e.target.value) : null })} className={`${sel} block w-full mt-1 bg-white`}>
                <option value="">—</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
          </div>

          {panelMsg && <p className={`mb-3 text-xs ${panelMsg.ok ? 'text-emerald-700' : 'text-red-600'}`}>{panelMsg.text}</p>}

          <div className="border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">AI 추출</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[doc.extractStatus]}`}>{DOC_STATUS.toUi(doc.extractStatus)}</span>
              </div>
              <div className="flex gap-1.5">
                {canExtract && aiSend !== false && (
                  <button onClick={extract} disabled={!!busy}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                    {busy === 'extract' ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI 로 읽기
                  </button>
                )}
                {doc.extractStatus === 'extracted' && fields.some((f) => f.target) && (
                  <button onClick={apply} disabled={!canApply || !!busy}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                    {busy === 'apply' ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} 검수 완료 · 반영
                  </button>
                )}
              </div>
            </div>

            {canExtract && aiSend === false && (
              <p className="mx-4 mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                AI 읽기 꺼짐 — 투자처 문서를 AI 로 보내지 않도록 관리자가 설정해 두었습니다. 숫자는 투자처 재무 탭에서 직접 넣으세요.
              </p>
            )}

            {busy === 'extract' ? (
              <p className="py-12 flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> 읽는 중...</p>
            ) : fields.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">{doc.kind === 'financials' ? '추출값 없음' : 'AI 로 읽는 문서는 재무제표뿐입니다 — 이 문서는 보관만 합니다'}</p>
            ) : (
              <>
              {kind === null && (
                <p className="mx-4 mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {period!.value} — 기간을 정할 수 없는 재무제표(분기 · 누적 등)라 반영하지 않습니다. 연간 · 상반기만 자동으로 넣습니다 — 투자처 재무 탭에서 기간을 골라 직접 넣으세요.
                </p>
              )}
              {kind === 'H1' && (
                <p className="mx-4 mt-3 rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">
                  {period!.value} — 상반기 재무제표입니다. 그 연도 「상반기」 줄에 넣습니다 — 연간 실적은 덮지 않습니다.
                </p>
              )}
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-400 border-b border-slate-100">
                  <th className="w-8" /><th className="text-left font-medium px-2 py-2">항목</th><th className="text-right font-medium px-2 py-2">값</th><th className="text-right font-medium px-4 py-2">확신도</th>
                </tr></thead>
                <tbody>
                  {fields.map((f) => {
                    const low = f.confidence !== null && f.confidence < LOW;
                    const editable = doc.extractStatus === 'extracted' && !!f.target?.startsWith('financials.');
                    const isPeriod = f.target === 'period';
                    const num = f.target && /^-?\d+$/.test(f.value) && !f.target.endsWith('fiscalYear');
                    return (
                      <tr key={f.id} className={`border-b border-slate-50 last:border-b-0 ${low || (isPeriod && kind === null) ? 'bg-amber-50/60' : ''}`}>
                        <td className="pl-4 py-2">
                          {editable ? (
                            <input type="checkbox" aria-label={`${f.label} 반영`} checked={checked.has(f.id)} className="rounded border-slate-300"
                              onChange={(e) => setChecked((s) => { const n = new Set(s); if (e.target.checked) n.add(f.id); else n.delete(f.id); return n; })} />
                          ) : f.isChecked ? <CheckCircle2 size={13} className="text-emerald-600" /> : null}
                        </td>
                        <td className="px-2 py-2 text-slate-700">{f.label}{isPeriod && kind === null && <span className="ml-1.5 text-xs font-semibold text-amber-700">기간 미정</span>}{isPeriod && kind === 'H1' && <span className="ml-1.5 text-xs font-semibold text-sky-700">상반기</span>}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-slate-900">
                          {editable ? (
                            <input aria-label={f.label} value={values[f.id] ?? (num ? Number(f.value).toLocaleString() : f.value)} onChange={(e) => setValues((v) => ({ ...v, [f.id]: e.target.value }))}
                              className="w-40 rounded border border-slate-200 px-1.5 py-0.5 text-right tabular-nums text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                          ) : num ? Number(f.value).toLocaleString() : f.value}
                        </td>
                        <td className={`px-4 py-2 text-right tabular-nums text-xs ${low ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>
                          {f.confidence === null ? '—' : `${Math.round(f.confidence * 100)}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </>
            )}
          </div>
        </SidePanel>
      )}
      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
